import React, { useState } from 'react';
import { Zap, Users, Globe, Star, CheckCircle, XCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

// Full gallery for SEO page - using verified working URLs
const DRONE_SHOW_GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png', titleKey: 'corporate' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png', titleKey: 'brandActivation' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/kvyua9ch_F3E666D8-2848-43FE-960B-B28ACE0CE2B7.png', titleKey: 'formation' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/jcm85461_IMG_1225.png', titleKey: 'nightShow' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/d3034cg8_6A963654-19C7-4164-B47B-66702E2CBB17.png', titleKey: 'massiveEvent' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/rvc6k1k9_D9E80893-54A2-4E3E-8B6C-CF39D72069E1.png', titleKey: 'realEstateLaunch' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/1t6l7xrw_784188E3-94EC-4024-AA54-F027CA0D9874.png', titleKey: 'weddingDrones' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zio3060d_9FD265EA-9199-4903-B7B9-E7CA7AB0CF82.png', titleKey: 'combinedShow' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/zdmzsrqn_6807D169-3301-4818-92FC-61CA37977AB4.png', titleKey: 'waterScreenStar' },
];

const galleryTitles = {
  es: { corporate: 'Show corporativo', brandActivation: 'Activación de marca con drones', formation: 'Formación de drones', nightShow: 'Show nocturno', massiveEvent: 'Evento masivo', realEstateLaunch: 'Lanzamiento inmobiliario', weddingDrones: 'Boda con drones', combinedShow: 'Show combinado', waterScreenStar: 'Estrella con water screen' },
  en: { corporate: 'Corporate show', brandActivation: 'Brand activation with drones', formation: 'Drone formation', nightShow: 'Night show', massiveEvent: 'Massive event', realEstateLaunch: 'Real estate launch', weddingDrones: 'Wedding with drones', combinedShow: 'Combined show', waterScreenStar: 'Star with water screen' },
};

const benefitIcons = [Zap, Users, Globe, Star];
const benefitKeys = ['visualImpact', 'massReach', 'ecoFriendly', 'customization'];

const ShowDeDronesPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { t, language } = useLanguage();
  const page = t('seoPages.droneShow');

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Show de Drones en México | Espectáculos Aéreos con Luz | NexAir" : "Drone Shows in Mexico | Aerial Light Spectacles | NexAir"}
        description={language === 'es' ? "Shows de drones profesionales en México. Cientos de drones sincronizados creando figuras, logos y mensajes en el cielo. Ideal para eventos corporativos, lanzamientos y celebraciones." : "Professional drone shows in Mexico. Hundreds of synchronized drones creating figures, logos and messages in the sky. Ideal for corporate events, launches and celebrations."}
        keywords={language === 'es' ? "show de drones mexico, espectaculo de drones, drones con luces, show aereo drones, eventos con drones, drones para eventos" : "drone show mexico, drone spectacle, drones with lights, aerial drone show, events with drones, drones for events"}
        canonicalUrl="https://nexair.com.mx/show-de-drones"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png"
        title={page.title}
        subtitle={page.subtitle}
        description={page.description}
      />
      
      {/* Benefits Section */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
          {page.whyChoose} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{page.whyChooseHighlight}</span>?
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitKeys.map((key, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{page.benefits[key].title}</h3>
                <p className="text-gray-400">{page.benefits[key].desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>
      
      {/* How it Works */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {page.howItWorks}
            </h2>
            <p className="text-gray-300 text-lg mb-6">
              {page.howItWorksDesc1}
            </p>
            <p className="text-gray-300 text-lg mb-6">
              {page.howItWorksDesc2}
            </p>
            <div className="space-y-3">
              {page.useCases.map((useCase, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                  <span className="text-gray-300">{useCase}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/kvyua9ch_F3E666D8-2848-43FE-960B-B28ACE0CE2B7.png"
              alt={page.howItWorks}
              className="rounded-2xl border border-white/10 w-full"
            />
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
          {page.galleryTitle}
        </h2>
        <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
          {page.galleryDesc}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {DRONE_SHOW_GALLERY.map((image, index) => (
            <div 
              key={index}
              className="aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/50 transition-all cursor-pointer"
              onClick={() => setLightboxImage({ ...image, title: galleryTitles[language][image.titleKey] })}
            >
              <img 
                src={image.url} 
                alt={galleryTitles[language][image.titleKey]}
                loading="lazy"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Stats */}
      <PageSection className="bg-gradient-to-b from-[#050a15] to-[#030810]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <div>
            <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">500+</div>
            <p className="text-gray-400">{page.stats.drones}</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">50+</div>
            <p className="text-gray-400">{page.stats.shows}</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">1M+</div>
            <p className="text-gray-400">{page.stats.spectators}</p>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2">100%</div>
            <p className="text-gray-400">{page.stats.satisfaction}</p>
          </div>
        </div>
      </PageSection>
      
      <PageCTA 
        title={page.ctaTitle}
        subtitle={page.ctaSubtitle}
        whatsappMessage={page.ctaWhatsapp}
      />

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <XCircle size={40} />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img 
              src={lightboxImage.url} 
              alt={lightboxImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-bold">{lightboxImage.title}</h3>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ShowDeDronesPage;
